import { Fragment, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  deviationItemLabel,
  deviationStatusLabel,
  deviationStatusTone,
  elementKindLabel,
  fmtCoord,
  fmtMm,
  fmtMmAbs,
  fmtTol,
  isSurfaceItem,
  matchedByLabel,
  type GeoDeviationReport,
  type GeoElementDeviation,
  type GeoOverride,
} from '@entities/geodesy';
import { Badge } from '@shared/ui/Badge';
import { IconPencil } from '@shared/ui/icons';
import {
  CellInput,
  Check,
  Chip,
  DataTable,
  FieldLabel,
  Muted,
  Num,
  Row,
  Stack,
  TableWrap,
  ToneRow,
} from './ui';

type Filter = 'all' | GeoElementDeviation['status'];

const Small = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
`;

/** Как получено положение (окружность по точкам, грани) — переносится по словам. */
const Derivation = styled.div`
  margin-top: 2px;
  max-width: 260px;
  font-size: 11.5px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const TolCell = styled.div`
  font-size: 12px;
  line-height: 1.45;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const Changed = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
`;

/** Строка-редактор под строкой отчёта: поля корректировки на всю ширину таблицы. */
const EditorRow = styled.tr`
  td {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const EditorGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};

  & > .point {
    flex: 1 1 150px;
  }
  & > .note {
    flex: 2 1 220px;
  }
`;

const CheckCell = styled.div`
  flex: 0 0 auto;
  padding-bottom: ${({ theme }) => theme.spacing(1.5)};
  white-space: nowrap;
`;

const SurfaceHint = styled(Muted)`
  flex: 1 1 200px;
  padding-bottom: ${({ theme }) => theme.spacing(1.5)};
`;

/** Метка элемента — она же кнопка раскрытия корректировки (всегда в первой колонке). */
const ToggleLabel = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(1.5)}`};
  margin-left: -${({ theme }) => theme.spacing(1.5)};
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: transparent;
  font: inherit;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  svg {
    color: ${({ theme }) => theme.colors.text.muted};
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.border};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  &[aria-expanded='true'] svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

export const emptyOverride = (element_id: string): GeoOverride => ({
  element_id,
  excluded: false,
  forced_point: null,
  forced_top_point: null,
  note: null,
});

export function isEmptyOverride(o: GeoOverride): boolean {
  return !o.excluded && !o.forced_point && !o.forced_top_point && !(o.note && o.note.trim());
}

/** Убрать пустые записи корректировок (всё по умолчанию). */
export function compactOverrides(list: GeoOverride[]): GeoOverride[] {
  return list
    .map((o) => ({
      ...o,
      forced_point: o.forced_point?.trim() ? o.forced_point.trim() : null,
      forced_top_point: o.forced_top_point?.trim() ? o.forced_top_point.trim() : null,
      note: o.note?.trim() ? o.note.trim() : null,
    }))
    .filter((o) => !isEmptyOverride(o));
}

function DevValue({ value, bad }: { value: number | null | undefined; bad: boolean }) {
  if (value == null) return <>—</>;
  return (
    <Num $tone={bad ? 'danger' : undefined} $strong={bad}>
      {fmtMm(value)}
    </Num>
  );
}

function MatchCell({ d }: { d: GeoElementDeviation }) {
  if (!d.actual) return <>—</>;
  return (
    <>
      {d.actual.name}
      {d.top && <Small>верх: {d.top.name}</Small>}
      <Small>
        {matchedByLabel(d.matched_by)}
        {d.match_distance_m != null && d.matched_by !== 'surface'
          ? `, ${fmtCoord(d.match_distance_m, 3)} м`
          : ''}
      </Small>
      {d.derivation && <Derivation>{d.derivation}</Derivation>}
    </>
  );
}

/** Элемент: метка (у угла контура — с маркой) и вид. */
function KindNote({ d }: { d: GeoElementDeviation }) {
  if (d.round) {
    return <Small>круглая конструкция ⌀ {fmtCoord(2 * d.round.radius_m, 3)} м</Small>;
  }
  if (d.contour) {
    // Точка верха в середине контура — строка поверхности с маркой контура.
    return <Small>{isSurfaceItem(d) ? 'отметка верха' : 'угол контура'}</Small>;
  }
  return <Small>{elementKindLabel(d.kind)}</Small>;
}

/**
 * Допуск граней угла контура, когда угол целиком не измерен (одна грань); у
 * круглой конструкции — всегда (план — смещение центра, грани — стена).
 */
function facesTol(d: GeoElementDeviation): string | null {
  const tol = d.faces?.find((f) => f.tol_mm != null)?.tol_mm;
  if (!d.faces || d.faces.length === 0 || (d.plan && !d.round)) return null;
  return `${d.round ? 'стена' : 'грани'}: ${fmtTol(tol ?? null)}`;
}

function StatusCell({ d, changed }: { d: GeoElementDeviation; changed?: boolean }) {
  return (
    <>
      <Badge $tone={deviationStatusTone(d.status)}>{deviationStatusLabel(d.status)}</Badge>
      {d.note && <Small>{d.note}</Small>}
      {changed && <Changed>есть корректировка</Changed>}
    </>
  );
}

function VerticalityValue({ d }: { d: GeoElementDeviation }) {
  if (!d.verticality) return <>—</>;
  return (
    <Num $tone={d.verticality.exceeded ? 'danger' : undefined} $strong={d.verticality.exceeded}>
      {fmtMmAbs(d.verticality.total_mm)}
    </Num>
  );
}

interface EditProps {
  overrides: GeoOverride[];
  onChange: (next: GeoOverride) => void;
  surveyNames: string[];
}

const EDIT_COLUMNS = 6;

/**
 * Таблица отклонений: план (ΔX/ΔY по МСК и вдоль/поперёк осей здания), высота,
 * вертикальность, допуски, вердикт и способ сопоставления. С `edit` — режим
 * корректировок шага 9: колонок меньше (план одной ячейкой «вдоль / поперёк»), а
 * поля корректировки раскрываются строкой под элементом — на всю ширину таблицы.
 */
export function DeviationTable({ report, edit }: { report: GeoDeviationReport; edit?: EditProps }) {
  const [filter, setFilter] = useState<Filter>(() =>
    report.items.some((i) => i.status === 'exceeded') ? 'exceeded' : 'all',
  );
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());

  const counts = useMemo(() => {
    const c: Record<Filter, number> = {
      all: report.items.length,
      ok: 0,
      exceeded: 0,
      not_measured: 0,
      excluded: 0,
    };
    for (const i of report.items) c[i.status] += 1;
    return c;
  }, [report.items]);

  const overrideById = useMemo(
    () => new Map((edit?.overrides ?? []).map((o) => [o.element_id, o])),
    [edit?.overrides],
  );
  const names = useMemo(() => new Set(edit?.surveyNames ?? []), [edit?.surveyNames]);

  // Фильтр опустел (например, превышения исправлены) — показываем всё.
  const active: Filter = filter !== 'all' && counts[filter] === 0 ? 'all' : filter;
  const rows = active === 'all' ? report.items : report.items.filter((i) => i.status === active);
  const filters: { key: Filter; label: string }[] = [
    { key: 'all', label: 'Все' },
    { key: 'exceeded', label: 'Превышения' },
    { key: 'ok', label: 'В допуске' },
    { key: 'not_measured', label: 'Не измерены' },
    { key: 'excluded', label: 'Исключённые' },
  ];

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <Stack $gap={3}>
      <Row $gap={2} role="group" aria-label="Фильтр строк">
        {filters
          .filter((f) => f.key === 'all' || counts[f.key] > 0)
          .map((f) => (
            <Chip
              key={f.key}
              type="button"
              $active={active === f.key}
              aria-pressed={active === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label} · {counts[f.key]}
            </Chip>
          ))}
      </Row>
      {edit && (
        <datalist id="geo-survey-names">
          {edit.surveyNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      )}
      {rows.length === 0 ? (
        <Muted>Строк нет.</Muted>
      ) : (
        <TableWrap $maxHeight={edit ? 640 : 560}>
          <DataTable>
            <thead>
              {edit ? (
                <tr>
                  <th>Элемент</th>
                  <th>Точка съёмки</th>
                  <th className="num">План, мм</th>
                  <th className="num">ΔH, мм</th>
                  <th className="num">Вертик.</th>
                  <th>Статус</th>
                </tr>
              ) : (
                <tr>
                  <th>Элемент</th>
                  <th>Точка съёмки</th>
                  <th className="num">ΔX</th>
                  <th className="num">ΔY</th>
                  <th className="num">Вдоль</th>
                  <th className="num">Поперёк</th>
                  <th className="num">ΔH</th>
                  <th className="num">Вертик.</th>
                  <th>Допуск, мм</th>
                  <th>Статус</th>
                </tr>
              )}
            </thead>
            <tbody>
              {rows.map((d) => {
                const o = overrideById.get(d.element_id) ?? emptyOverride(d.element_id);
                const changed = edit ? !isEmptyOverride(o) : false;
                const tone =
                  d.status === 'exceeded'
                    ? 'danger'
                    : changed
                      ? 'warning'
                      : d.status === 'excluded'
                        ? 'muted'
                        : undefined;
                const planBad = Boolean(d.plan?.exceeded);
                if (edit) {
                  const surface = isSurfaceItem(d);
                  const isOpen = open.has(d.element_id);
                  const editorId = `geo-corr-${d.element_id}`;
                  const forcedBad = Boolean(o.forced_point && !names.has(o.forced_point.trim()));
                  const topBad = Boolean(
                    o.forced_top_point && !names.has(o.forced_top_point.trim()),
                  );
                  return (
                    <Fragment key={d.element_id}>
                      <ToneRow $tone={tone}>
                        <td>
                          <ToggleLabel
                            type="button"
                            aria-expanded={isOpen}
                            aria-controls={editorId}
                            aria-label={`Корректировка элемента ${deviationItemLabel(d)}`}
                            onClick={() => toggle(d.element_id)}
                          >
                            {deviationItemLabel(d)}
                            <IconPencil size={14} />
                          </ToggleLabel>
                          <KindNote d={d} />
                        </td>
                        <td>
                          <MatchCell d={d} />
                        </td>
                        <td className="num">
                          {d.plan ? (
                            <>
                              <DevValue value={d.plan.along_mm} bad={planBad} /> /{' '}
                              <DevValue value={d.plan.across_mm} bad={planBad} />
                            </>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="num">
                          <DevValue value={d.height?.dh_mm} bad={Boolean(d.height?.exceeded)} />
                        </td>
                        <td className="num">
                          <VerticalityValue d={d} />
                        </td>
                        <td>
                          <StatusCell d={d} changed={changed} />
                        </td>
                      </ToneRow>
                      {isOpen && (
                        <EditorRow id={editorId}>
                          <td colSpan={EDIT_COLUMNS}>
                            <EditorGrid>
                              <CheckCell>
                                <Check
                                  checked={o.excluded}
                                  onChange={(v) => edit.onChange({ ...o, excluded: v })}
                                >
                                  исключить из схемы
                                </Check>
                              </CheckCell>
                              {surface ? (
                                <SurfaceHint>
                                  Точка на поверхности проекта — сопоставление не требуется.
                                </SurfaceHint>
                              ) : d.round ? (
                                <FieldLabel className="point">
                                  Точка стены вручную (стена ушла дальше 8 см)
                                  <CellInput
                                    value={o.forced_point ?? ''}
                                    list="geo-survey-names"
                                    placeholder="авто — по окружности"
                                    aria-invalid={forcedBad}
                                    title={forcedBad ? 'Такой точки нет в съёмке' : undefined}
                                    onChange={(e) =>
                                      edit.onChange({
                                        ...o,
                                        forced_point: e.target.value || null,
                                      })
                                    }
                                  />
                                </FieldLabel>
                              ) : d.contour ? (
                                <FieldLabel className="point">
                                  Точка в самом углу (обе грани)
                                  <CellInput
                                    value={o.forced_point ?? ''}
                                    list="geo-survey-names"
                                    placeholder="авто — по граням"
                                    aria-invalid={forcedBad}
                                    title={forcedBad ? 'Такой точки нет в съёмке' : undefined}
                                    onChange={(e) =>
                                      edit.onChange({
                                        ...o,
                                        forced_point: e.target.value || null,
                                      })
                                    }
                                  />
                                </FieldLabel>
                              ) : (
                                <>
                                  <FieldLabel className="point">
                                    Точка съёмки вручную
                                    <CellInput
                                      value={o.forced_point ?? ''}
                                      list="geo-survey-names"
                                      placeholder="авто"
                                      aria-invalid={forcedBad}
                                      title={forcedBad ? 'Такой точки нет в съёмке' : undefined}
                                      onChange={(e) =>
                                        edit.onChange({
                                          ...o,
                                          forced_point: e.target.value || null,
                                        })
                                      }
                                    />
                                  </FieldLabel>
                                  <FieldLabel className="point">
                                    Точка верха (вертикальность)
                                    <CellInput
                                      value={o.forced_top_point ?? ''}
                                      list="geo-survey-names"
                                      placeholder="авто"
                                      aria-invalid={topBad}
                                      title={topBad ? 'Такой точки нет в съёмке' : undefined}
                                      onChange={(e) =>
                                        edit.onChange({
                                          ...o,
                                          forced_top_point: e.target.value || null,
                                        })
                                      }
                                    />
                                  </FieldLabel>
                                </>
                              )}
                              <FieldLabel className="note">
                                {o.excluded ? 'Причина исключения (обязательно)' : 'Примечание'}
                                <CellInput
                                  value={o.note ?? ''}
                                  placeholder={o.excluded ? 'например: свая будет перебита' : ''}
                                  aria-invalid={o.excluded && !o.note?.trim()}
                                  title={
                                    o.excluded && !o.note?.trim()
                                      ? 'Без причины элемент не исключить: она попадёт в журнал и в контроль перед передачей'
                                      : undefined
                                  }
                                  onChange={(e) =>
                                    edit.onChange({ ...o, note: e.target.value || null })
                                  }
                                />
                              </FieldLabel>
                            </EditorGrid>
                          </td>
                        </EditorRow>
                      )}
                    </Fragment>
                  );
                }

                return (
                  <ToneRow key={d.element_id} $tone={tone}>
                    <td>
                      <b>{deviationItemLabel(d)}</b>
                      <KindNote d={d} />
                    </td>
                    <td>
                      <MatchCell d={d} />
                    </td>
                    <td className="num">
                      <DevValue value={d.plan?.dx_mm} bad={false} />
                    </td>
                    <td className="num">
                      <DevValue value={d.plan?.dy_mm} bad={false} />
                    </td>
                    <td className="num">
                      <DevValue value={d.plan?.along_mm} bad={planBad} />
                    </td>
                    <td className="num">
                      <DevValue value={d.plan?.across_mm} bad={planBad} />
                    </td>
                    <td className="num">
                      <DevValue value={d.height?.dh_mm} bad={Boolean(d.height?.exceeded)} />
                    </td>
                    <td className="num">
                      <VerticalityValue d={d} />
                    </td>
                    <td>
                      <TolCell>
                        {d.plan && (
                          <div>
                            {d.round ? 'центр' : 'план'}: {fmtTol(d.plan.tol_along_mm)} /{' '}
                            {fmtTol(d.plan.tol_across_mm)}
                          </div>
                        )}
                        {d.height && (
                          <div>высота: {fmtTol(d.height.tol_plus_mm, d.height.tol_minus_mm)}</div>
                        )}
                        {d.verticality && <div>вертик.: {fmtMmAbs(d.verticality.tol_mm)}</div>}
                        {facesTol(d) && <div>{facesTol(d)}</div>}
                        {!d.plan && !d.height && !d.verticality && !facesTol(d) && '—'}
                      </TolCell>
                    </td>
                    <td>
                      <StatusCell d={d} />
                    </td>
                  </ToneRow>
                );
              })}
            </tbody>
          </DataTable>
        </TableWrap>
      )}
    </Stack>
  );
}
